import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Paper, Modal, Backdrop, Fade, Button } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';

export type MessageType = 'success' | 'error' | 'info' | 'warning';

interface MessageModalProps {
	type: MessageType;
	message: string;
	closeModal?: () => void;
	open: boolean;
	title?: string; // Optional title for the modal
}

const iconMap = {
	success: <CheckCircleOutlinedIcon sx={{ fontSize: 28 }} />,
	error: <ErrorOutlineOutlinedIcon sx={{ fontSize: 28 }} />,
	info: <InfoOutlinedIcon sx={{ fontSize: 28 }} />,
	warning: <WarningAmberOutlinedIcon sx={{ fontSize: 28 }} />,
};

const titleMap = {
	success: 'Success',
	error: 'Error',
	info: 'Information',
	warning: 'Warning',
};

const colorMap = {
	success: {
		main: '#2e7d32', // Green
		light: '#e8f5e9',
		border: '#81c784',
		icon: '#2e7d32',
		button: '#2e7d32',
	},
	error: {
		main: '#d32f2f', // Red
		light: '#ffebee',
		border: '#e57373',
		icon: '#d32f2f',
		button: '#d32f2f',
	},
	info: {
		main: '#0288d1', // Blue
		light: '#e1f5fe',
		border: '#4fc3f7',
		icon: '#0288d1',
		button: '#0288d1',
	},
	warning: {
		main: '#ed6c02', // Orange
		light: '#fff3e0',
		border: '#ffb74d',
		icon: '#ed6c02',
		button: '#ed6c02',
	},
};

const MessageModal: React.FC<MessageModalProps> = ({ type = 'info', message, closeModal, open, title }) => {
	// State to track if the message should be shown with animation
	const [visible, setVisible] = useState(false);

	// Set visible state to true after a tiny delay for smooth animation
	useEffect(() => {
		if (open) {
			const timer = setTimeout(() => {
				setVisible(true);
			}, 50);
			return () => clearTimeout(timer);
		} else {
			setVisible(false);
		}
	}, [open]);

	return (
		<Modal
			open={open}
			onClose={closeModal}
			closeAfterTransition
			slots={{
				backdrop: Backdrop,
			}}
			slotProps={{
				backdrop: {
					timeout: 500,
					style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
				},
			}}
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Fade in={open}>
				<Paper
					elevation={8}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						borderRadius: '12px',
						backgroundColor: '#ffffff',
						boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
						maxWidth: { xs: '92vw', sm: '400px' },
						width: { xs: '92vw', sm: '400px' },
						position: 'relative',
						overflow: 'hidden',
						opacity: visible ? 1 : 0,
						transform: visible ? 'scale(1)' : 'scale(0.95)',
						transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
						border: `1px solid ${colorMap[type].border}`,
					}}
				>
					{/* Header section */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							backgroundColor: colorMap[type].light,
							py: 1.5,
							px: 2,
							borderBottom: `1px solid ${colorMap[type].border}`,
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Box
								sx={{
									color: colorMap[type].icon,
									mr: 1.5,
								}}
							>
								{iconMap[type]}
							</Box>
							<Typography
								variant='subtitle1'
								sx={{
									fontWeight: 600,
									color: colorMap[type].main,
								}}
							>
								{title ?? titleMap[type]}
							</Typography>
						</Box>

						{closeModal && (
							<IconButton
								size='small'
								onClick={closeModal}
								aria-label='close'
								sx={{
									color: 'rgba(0, 0, 0, 0.54)',
									'&:hover': {
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
									},
								}}
							>
								<CloseIcon fontSize='small' />
							</IconButton>
						)}
					</Box>

					{/* Message body */}
					<Box
						sx={{
							p: 2.5,
							backgroundColor: '#ffffff',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								color: 'text.primary',
								lineHeight: 1.6,
							}}
						>
							{message}
						</Typography>
					</Box>

					{/* Button section */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							p: 2,
							pt: 1,
							backgroundColor: '#ffffff',
						}}
					>
						<Button
							variant='contained'
							onClick={closeModal}
							disableElevation
							sx={{
								backgroundColor: colorMap[type].button,
								color: '#ffffff',
								px: 3,
								py: 0.75,
								fontSize: '0.875rem',
								textTransform: 'none',
								borderRadius: '6px',
								'&:hover': {
									backgroundColor: colorMap[type].main,
									opacity: 0.9,
								},
								fontWeight: 500,
							}}
						>
							OK
						</Button>
					</Box>
				</Paper>
			</Fade>
		</Modal>
	);
};

export default MessageModal;
